import { useState, useRef, useEffect } from 'react';
import './Chatbot.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/* ── Validation helpers for the lead-capture flow ─────────────────────── */
const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((v || '').trim());
const isValidPhone = (v) => {
  const digits = (v || '').replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 13;
};

/* ── FAQ knowledge base (instant answers, no API needed) ─────────────── */
const FAQS = [
  {
    keys: ['service', 'servic', 'offer', 'what do you do', 'seivai', 'enna panreenga', 'enna pannuv', 'velai'],
    q: 'What services do you offer?',
    a: 'We offer 5 core services:\n\n🌐 Website Development – modern, fast, responsive websites\n📈 Digital Marketing – SEO, social media, paid ads\n🎨 Graphic Design – branding, posters, creatives\n🤖 AI Solutions – chatbots & automation for your business\n🛠️ Custom Software Development – bespoke apps & tools built for your workflow\n\nCheck the Services page for full details!',
  },
  {
    keys: ['price', 'prise', 'pricing', 'cost', 'plan', 'package', 'how much', 'rate', 'fees', 'evlo', 'vilai', 'amount', 'charge', 'budget'],
    q: 'What are your pricing plans?',
    a: 'We have 3 plans (monthly / annual):\n\n🥈 Silver – ₹12,499/mo (₹9,999/mo billed annually)\n🥇 Gold – ₹24,999/mo (₹19,999/mo annually)\n💎 Diamond – ₹44,999/mo (₹35,999/mo annually)\n\nAnnual billing saves you up to 20%! Contact us for a custom quote.',
  },
  {
    keys: ['contact', 'reach', 'email', 'mail', 'phone', 'number', 'call', 'whatsapp', 'talk', 'pesanum', 'number kudunga'],
    q: 'How can I contact you?',
    a: 'You can reach us anytime:\n\n📧 Email: aerialroot1@gmail.com\n💬 WhatsApp: +91 90802 67314\n📸 Instagram: @aerialroot\n\nOr just fill the form on our Contact page — we usually reply within 24 hours!',
  },
  {
    keys: ['website', 'web development', 'web design', 'site build'],
    q: 'Do you build websites?',
    a: 'Yes! We build modern, mobile-friendly, SEO-ready websites — business sites, portfolios, landing pages and more. Every site is custom designed to match your brand. Contact us with your requirements for a free quote!',
  },
  {
    keys: ['seo', 'social media', 'marketing', 'ads', 'instagram growth', 'google'],
    q: 'What does digital marketing include?',
    a: 'Our digital marketing covers:\n\n• SEO (rank higher on Google)\n• Social media management (Instagram, Facebook & more)\n• Paid ad campaigns (Meta & Google Ads)\n• Content & creatives\n\nWe use AI-powered strategies to get real, measurable results.',
  },
  {
    keys: ['ai', 'chatbot', 'automation', 'artificial intelligence'],
    q: 'What are AI Solutions?',
    a: 'We build AI tools for businesses — chatbots (like me! 🤖), workflow automation, and AI-assisted marketing. These save time, cut costs, and keep your customers engaged 24/7. Ask us for a demo!',
  },
  {
    keys: ['custom software', 'software development', 'web app', 'web application', 'internal tool', 'saas', 'api integration', 'dashboard'],
    q: 'Do you build custom software?',
    a: 'Yes! We design and build custom software — web applications, internal tools, dashboards, API integrations, and full SaaS products — engineered around your exact workflow instead of forcing you into a generic tool. Contact us with your requirements for a free quote!',
  },
  {
    keys: ['portfolio', 'work', 'client', 'example', 'project', 'past'],
    q: 'Can I see your past work?',
    a: 'Of course! Head to our Portfolio page to see designs and campaigns we\'ve created for clients like Kulambu Kitchen, British School and more. Our Videos page also showcases our video work.',
  },
  {
    keys: ['time', 'how long', 'duration', 'deliver', 'timeline', 'days', 'evlo naal', 'eppo mudiyum', 'weeks'],
    q: 'How long does a project take?',
    a: 'It depends on scope:\n\n• Landing page: ~1 week\n• Full business website: 2–4 weeks\n• Branding/design packages: 1–2 weeks\n• Marketing: ongoing monthly plans\n\nWe\'ll give you a clear timeline before we start.',
  },
  {
    keys: ['academy', 'course', 'learn', 'training', 'class'],
    q: 'Do you offer courses?',
    a: 'Yes! We offer training in digital marketing and design skills. Message us on WhatsApp (+91 90802 67314) for batch details and enrollment.',
  },
  {
    keys: ['location', 'where', 'address', 'office', 'based', 'enga irukk', 'trichy', 'map'],
    q: 'Where are you located?',
    a: 'Our office:\n\n📍 D-11, Thilai Nagar, 6th Cross Rd, Trichy - 620018, Tamil Nadu\n\nWe work with clients everywhere — all services available remotely too! Reach us at aerialroot1@gmail.com or WhatsApp +91 90802 67314.',
  },
];

const GREETING = {
  role: 'bot',
  text: 'Hi! 👋 Welcome to AerialRoot! Before we start, could I get your name?',
};


/* ── Small talk (greetings, thanks, bye — feels like a real AI) ──────── */
const SMALLTALK = [
  {
    match: /^(hi+|hii+|hello+|hey+|hai|helo|vanakkam|hola|yo)\b|good (morning|afternoon|evening)/i,
    replies: [
      'Hi there! 👋 Great to see you! How can I help you today?',
      'Hello! 😊 Welcome to AerialRoot! What would you like to know?',
      'Hey! 👋 Vanakkam! Ask me anything about our services or pricing!',
    ],
  },
  {
    match: /how are you|eppadi irukk|how r u|hru\b|how('?s| is) it going|what'?s up|wassup|sup\b/i,
    replies: [
      "I'm doing great, thanks for asking! 😄 I'm always happy to chat. How can I help you?",
      "All good here! 🤖 Running at 100% and ready to help. What can I do for you?",
    ],
  },
  {
    match: /thank|thanks|thx|nandri|tq\b|thnk/i,
    replies: [
      "You're most welcome! 😊 Happy to help anytime!",
      'Anytime! 🙌 Feel free to ask if you have more questions!',
      'Glad I could help! 💚 Nandri for chatting with me!',
    ],
  },
  {
    match: /^(bye+|goodbye|good ?night|see (you|ya)|tata|poitu var|catch you later|cya|gtg)\b/i,
    replies: [
      'Bye! 👋 Have a wonderful day! Come back anytime!',
      'Goodbye! 😊 Thanks for visiting AerialRoot — see you soon!',
      'Take care! 💚 Ping me whenever you need help!',
    ],
  },
  {
    match: /who are you|what are you|your name|unga per|nee yaru|about you/i,
    replies: [
      "I'm AerialBot 🤖 — AerialRoot's friendly assistant! I can answer questions about our services, pricing, projects and more. Try me!",
    ],
  },
  {
    match: /^(ok+|okay|okey|k+|hmm+|cool|nice|super|great|awesome|good|fine|semma|mass)\W*$/i,
    replies: [
      'Great! 😊 Anything else you\'d like to know?',
      'Cool! 👍 Ask me anything else — services, pricing, timelines!',
      'Super! 💚 I\'m here if you need anything else!',
    ],
  },
  {
    match: /^(yes|yeah|yep|aama|ya)\W*$/i,
    replies: ['Awesome! 😄 Go ahead, ask your question!'],
  },
  {
    match: /^(no+|nope|illa|venam)\W*$/i,
    replies: ['No problem! 😊 I\'m here whenever you need me. Have a great day!'],
  },
  {
    match: /help|enna panna|start|begin/i,
    replies: [
      'Sure! 🙌 I can tell you about:\n\n• Our services (web, marketing, design, AI)\n• Pricing plans\n• Project timelines\n• Contact details\n• Our portfolio\n\nJust ask away!',
    ],
  },
];

function matchSmallTalk(input) {
  const t = input.trim();
  for (const s of SMALLTALK) {
    if (s.match.test(t)) {
      return s.replies[Math.floor(Math.random() * s.replies.length)];
    }
  }
  return null;
}

function scoreFaq(f, t) {
  let score = 0;
  for (const k of f.keys) if (t.includes(k)) score += k.length * 2;
  // token-level fuzzy: partial word overlap handles typos like "servise"
  const words = t.split(/\s+/).filter((w) => w.length > 3);
  for (const w of words) {
    for (const k of f.keys) {
      const kw = k.split(' ')[0];
      if (kw.length > 3 && (w.startsWith(kw.slice(0, 4)) || kw.startsWith(w.slice(0, 4)))) score += 3;
    }
  }
  return score;
}

function matchFaq(input) {
  const t = input.toLowerCase();
  let best = null;
  let bestScore = 0;
  for (const f of FAQS) {
    const s = scoreFaq(f, t);
    if (s > bestScore) { bestScore = s; best = f; }
  }
  return bestScore >= 4 ? best : null;
}

function closestFaqs(input, n = 3) {
  const t = input.toLowerCase();
  return FAQS
    .map((f) => ({ f, s: scoreFaq(f, t) }))
    .sort((a, b) => b.s - a.s)
    .slice(0, n)
    .map((x) => x.f.q);
}

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const bodyRef = useRef(null);
  const sessionIdRef = useRef(
    (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`)
  );

  // Lead-capture flow: name -> phone -> email -> chat
  const [stage, setStage] = useState('name');
  const leadRef = useRef({ name: '', phone: '', email: '' });

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, typing, open]);

  // Fire-and-forget: store the exchange so it's visible in the admin panel
  function logChat(userText, botText, source) {
    fetch(`${API_URL}/api/chat/log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: sessionIdRef.current,
        message: userText,
        reply: botText,
        source,
      }),
    }).catch(() => {});
  }

  // Fire-and-forget: store the visitor's name/phone/email
  function saveLead() {
    fetch(`${API_URL}/api/chat/lead`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: sessionIdRef.current,
        name: leadRef.current.name,
        phone: leadRef.current.phone,
        email: leadRef.current.email,
      }),
    }).catch(() => {});
  }

  const quickQuestions = FAQS.slice(0, 4).map((f) => f.q);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || typing) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text: q }]);

    // ── Lead-capture steps (before any Q&A) ──────────────────────────────
    if (stage === 'name') {
      leadRef.current.name = q;
      setTyping(true);
      setTimeout(() => {
        setMessages((m) => [...m, { role: 'bot', text: `Nice to meet you, ${q}! 😊 Could you share your phone number?` }]);
        setTyping(false);
        setStage('phone');
      }, 400);
      return;
    }

    if (stage === 'phone') {
      if (!isValidPhone(q)) {
        setTyping(true);
        setTimeout(() => {
          setMessages((m) => [...m, { role: 'bot', text: '⚠️ That doesn\'t look like a valid phone number. Please enter a valid 10-digit number (e.g. +91 9876543210).' }]);
          setTyping(false);
        }, 300);
        return;
      }
      leadRef.current.phone = q;
      setTyping(true);
      setTimeout(() => {
        setMessages((m) => [...m, { role: 'bot', text: 'Great, thanks! 📱 And your email id?' }]);
        setTyping(false);
        setStage('email');
      }, 400);
      return;
    }

    if (stage === 'email') {
      if (!isValidEmail(q)) {
        setTyping(true);
        setTimeout(() => {
          setMessages((m) => [...m, { role: 'bot', text: '⚠️ That doesn\'t look like a valid email address. Please enter a valid email (e.g. name@example.com).' }]);
          setTyping(false);
        }, 300);
        return;
      }
      leadRef.current.email = q;
      saveLead();
      setTyping(true);
      setTimeout(() => {
        setMessages((m) => [...m, {
          role: 'bot',
          text: `Perfect, thank you ${leadRef.current.name}! 🙌 Now ask me anything about our services, pricing, or projects — or tap a question below!`,
          suggest: quickQuestions,
        }]);
        setTyping(false);
        setStage('chat');
      }, 400);
      return;
    }

    // ── Normal Q&A flow (after details are collected) ────────────────────
    setTyping(true);

    // 1) Try AI backend (if running & API key configured)
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 12000);
      const res = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: q }),
        signal: ctrl.signal,
      });
      clearTimeout(timer);
      if (res.ok) {
        const data = await res.json();
        if (data.reply) {
          setMessages((m) => [...m, { role: 'bot', text: data.reply }]);
          setTyping(false);
          logChat(q, data.reply, 'ai');
          return;
        }
      }
    } catch (_) { /* backend offline — fall back to local FAQ */ }

    // 2) Local small talk + FAQ fallback (always replies!)
    const small = matchSmallTalk(q);
    const faq = small ? null : matchFaq(q);
    let botMsg;
    let source;
    if (small) {
      botMsg = { role: 'bot', text: small };
      source = 'smalltalk';
    } else if (faq) {
      botMsg = { role: 'bot', text: faq.a };
      source = 'faq';
    } else {
      botMsg = {
        role: 'bot',
        text: 'Good question! 🤔 I may not have the exact answer for that, but our team definitely does:\n\n📧 aerialroot1@gmail.com\n💬 WhatsApp: +91 90802 67314\n\nMeanwhile, maybe one of these helps? 👇',
        suggest: closestFaqs(q),
      };
      source = 'unmatched';
    }
    setTimeout(() => {
      setMessages((m) => [...m, botMsg]);
      setTyping(false);
      logChat(q, botMsg.text, source);
    }, 500);
  };

  return (
    <>
      {/* Floating toggle button */}
      <button
        className={`cb-fab ${open ? 'cb-fab-open' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? 'Close chat' : 'Open chat'}
      >
        {open ? '✕' : <img src="chatbot-icon.png" alt="Chat" className="cb-fab-img" />}
      </button>

      {/* Chat window */}
      <div className={`cb-window ${open ? 'cb-show' : ''}`}>
        <div className="cb-header">
          <div className="cb-avatar"><img src="chatbot-icon.png" alt="AerialBot" /></div>
          <div>
            <div className="cb-title">AerialBot</div>
            <div className="cb-status"><span className="cb-dot" /> Online — ask me anything</div>
          </div>
        </div>

        <div className="cb-body" ref={bodyRef}>
          {messages.map((m, i) => (
            <div key={i} className={`cb-msg-wrap ${m.role === 'user' ? 'cb-wrap-user' : ''}`}>
              <div className={`cb-msg ${m.role === 'user' ? 'cb-user' : 'cb-bot'}`}>
                {m.text}
              </div>
              {m.suggest && (
                <div className="cb-quick">
                  {m.suggest.map((s) => (
                    <button key={s} className="cb-chip" onClick={() => send(s)}>{s}</button>
                  ))}
                </div>
              )}
            </div>
          ))}
          {typing && (
            <div className="cb-msg cb-bot cb-typing">
              <span /><span /><span />
            </div>
          )}
        </div>

        <div className="cb-input-row">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()}
            placeholder={
              stage === 'name' ? 'Type your name…' :
              stage === 'phone' ? 'Type your phone number…' :
              stage === 'email' ? 'Type your email id…' :
              'Type your question…'
            }
          />
          <button className="cb-send" onClick={() => send()} disabled={typing}>➤</button>
        </div>
      </div>
    </>
  );
}
